import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { NewsItem } from "@orvis/core";
import { trackEvent } from "../analytics.js";

/**
 * Which news outlets the user has switched off. Stored as a blocklist (not
 * an allowlist) so a source added server-side later shows up by default.
 * Keyed by the source *name* — that's what `NewsItem.source` carries.
 */

const STORAGE_KEY = "orvis:news-hidden";

interface NewsPrefsCtx {
  isHidden: (source: string) => boolean;
  toggle: (source: string) => void;
  showAll: () => void;
  hiddenCount: number;
  /** Items from sources the user hasn't hidden. */
  visible: (items: readonly NewsItem[]) => NewsItem[];
}

const Ctx = createContext<NewsPrefsCtx | undefined>(undefined);

function read(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as unknown;
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === "string") : [];
  } catch {
    return [];
  }
}

export function NewsPrefsProvider({ children }: { children: ReactNode }) {
  const [hidden, setHidden] = useState<string[]>(() => read());
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(hidden));
    } catch { /* ignore */ }
  }, [hidden]);

  const isHidden = useCallback((source: string) => hidden.includes(source), [hidden]);
  const toggle = useCallback((source: string) => {
    setHidden((prev) => {
      const wasHidden = prev.includes(source);
      trackEvent("news_source_toggle", { source, action: wasHidden ? "show" : "hide" });
      return wasHidden ? prev.filter((s) => s !== source) : [...prev, source];
    });
  }, []);
  const showAll = useCallback(() => setHidden([]), []);
  const visible = useCallback(
    (items: readonly NewsItem[]) => items.filter((n) => !n.source || !hidden.includes(n.source)),
    [hidden]
  );

  const value = useMemo<NewsPrefsCtx>(
    () => ({ isHidden, toggle, showAll, hiddenCount: hidden.length, visible }),
    [isHidden, toggle, showAll, hidden.length, visible]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNewsPrefs(): NewsPrefsCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useNewsPrefs must be used inside <NewsPrefsProvider>");
  return v;
}
